import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircle2,
  AlertTriangle,
  FileText,
  ClipboardList,
  Download,
  ArrowRight,
  Loader2,
  Shield,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Send,
  PartyPopper,
  Clock,
  Upload,
  FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AutoRevisionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [ds160Generated, setDs160Generated] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Check if user is on autoservicio plan
      const planCheck = await axios.get(`${API}/autoservicio/check-plan`);
      if (!planCheck.data.is_autoservicio) {
        toast.error('Esta página es solo para plan Estándar');
        navigate('/dashboard');
        return;
      }

      // Get case data
      const caseRes = await axios.get(`${API}/cases/my-case`);
      setCaseData(caseRes.data);

      // Check if ready to submit
      const submitCheck = await axios.get(`${API}/autoservicio/check-ready-to-submit`);
      setSubmitStatus(submitCheck.data);

      // Get existing review if any
      try {
        const reviewRes = await axios.get(`${API}/autoservicio/revision-status`);
        if (reviewRes.data.has_review) {
          setReviewData(reviewRes.data.review);
        }
      } catch (e) {
        // No review yet
      }

      // Get DS-160 if generated
      try {
        const ds160Res = await axios.get(`${API}/autoservicio/ds160-generado`);
        setDs160Generated(ds160Res.data);
      } catch (e) {
        // Not generated yet
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRevisionAutomatica = async () => {
    try {
      setProcessing(true);
      const res = await axios.post(`${API}/autoservicio/revision-automatica`);
      
      if (res.data.success) {
        setReviewData(res.data.review);
        toast.success('Revisión completada');
        
        if (res.data.can_proceed) {
          toast.success('¡Puedes continuar al siguiente paso!');
        }
        
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la revisión');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerarDS160 = async () => {
    try {
      setProcessing(true);
      const res = await axios.post(`${API}/autoservicio/generar-ds160`);
      
      if (res.data.success) {
        setDs160Generated(res.data.summary);
        toast.success('¡Tu DS-160 ha sido generado!');
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al generar el DS-160');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalizarYEnviar = async () => {
    try {
      setProcessing(true);
      const res = await axios.post(`${API}/autoservicio/finalizar-y-enviar`);
      
      if (res.data.success) {
        setShowSuccessModal(true);
        toast.success('¡Información enviada correctamente!');
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la información');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const ds160Data = caseData?.ds160_data || {};
  const ds160Progress = ds160Data.completed_sections?.length || 0;
  const documentsCount = submitStatus?.documents_count || 0;
  const hasMinDocs = submitStatus?.has_min_docs || false;
  const isAlreadySubmitted = submitStatus?.already_submitted || caseData?.status === "pendiente_admin";
  const isProcessed = caseData?.status === "procesado";

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="autorevision-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">
                Autoservicio
              </h1>
              <p className="text-navy-500">Plan Estándar - Proceso automático</p>
            </div>
          </div>
          
          {/* Plan Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 mt-2">
            <Shield className="w-4 h-4" />
            Plan Estándar - Sin asesor humano
          </div>
        </div>

        {/* Already Submitted Status */}
        {isAlreadySubmitted && !isProcessed && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-blue-900">Tu caso está en revisión</h2>
                <p className="text-blue-700">
                  Tu información fue enviada el {submitStatus?.submitted_at ? new Date(submitStatus.submitted_at).toLocaleDateString('es-ES') : new Date(caseData?.submitted_at).toLocaleDateString('es-ES')}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  Te contactaremos pronto para finalizar tu proceso.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Processed Status */}
        {isProcessed && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-emerald-900">¡Tu DS-160 fue procesado!</h2>
                <p className="text-emerald-700">
                  Hemos completado el llenado de tu formulario oficial.
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Revisa tu correo para los próximos pasos.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress Summary Card */}
        <div className="bg-white rounded-2xl border border-navy-100 p-6 mb-6">
          <h2 className="font-semibold text-navy-800 mb-4">Tu Progreso</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Documents Progress */}
            <div className={`p-4 rounded-xl ${hasMinDocs ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasMinDocs ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  <Upload className={`w-5 h-5 ${hasMinDocs ? 'text-emerald-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className={`text-sm ${hasMinDocs ? 'text-emerald-600' : 'text-amber-600'}`}>Documentos</p>
                  <p className={`text-xl font-bold ${hasMinDocs ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {documentsCount} subidos
                  </p>
                </div>
              </div>
              {!hasMinDocs && (
                <p className="text-xs text-amber-600 mt-2">Mínimo 2 documentos requeridos</p>
              )}
            </div>
            
            {/* DS-160 Progress */}
            <div className={`p-4 rounded-xl ${ds160Progress >= 6 ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ds160Progress >= 6 ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                  <FileText className={`w-5 h-5 ${ds160Progress >= 6 ? 'text-emerald-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <p className={`text-sm ${ds160Progress >= 6 ? 'text-emerald-600' : 'text-blue-600'}`}>DS-160</p>
                  <p className={`text-xl font-bold ${ds160Progress >= 6 ? 'text-emerald-700' : 'text-blue-700'}`}>
                    {ds160Progress}/12 secciones
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/documents')}
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir Documentos
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/ds160-assistant')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Llenar DS-160
            </Button>
          </div>
        </div>

        {/* MAIN ACTION: Finalizar y Enviar */}
        {!isAlreadySubmitted && !isProcessed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl border-2 p-6 mb-6 ${
              hasMinDocs 
                ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-300' 
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                hasMinDocs ? 'bg-emerald-500' : 'bg-gray-300'
              }`}>
                <Send className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-navy-900">Finalizar y Enviar</h2>
                <p className="text-navy-600">
                  {hasMinDocs 
                    ? 'Tu información está lista para ser enviada a nuestro equipo'
                    : 'Completa los requisitos mínimos para enviar tu información'
                  }
                </p>
              </div>
            </div>

            {/* Requirements Checklist */}
            <div className="space-y-2 mb-6">
              <div className={`flex items-center gap-2 ${hasMinDocs ? 'text-emerald-700' : 'text-gray-500'}`}>
                {hasMinDocs ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
                <span>Mínimo 2 documentos subidos ({documentsCount}/2)</span>
              </div>
              <div className={`flex items-center gap-2 ${ds160Progress > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>
                {ds160Progress > 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
                <span>DS-160 iniciado ({ds160Progress}/12 secciones) - Opcional pero recomendado</span>
              </div>
            </div>

            <Button
              onClick={handleFinalizarYEnviar}
              disabled={!hasMinDocs || processing}
              className={`w-full py-6 text-lg font-bold ${
                hasMinDocs 
                  ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="submit-case-btn"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              {processing ? 'Enviando...' : 'Finalizar y Enviar para Revisión'}
            </Button>

            {!hasMinDocs && (
              <p className="text-center text-sm text-gray-500 mt-3">
                Sube al menos 2 documentos para habilitar el envío
              </p>
            )}
          </motion.div>
        )}

        {/* Info for Standard Plan */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <h3 className="font-medium text-gray-800 mb-2">Plan Estándar - Autoservicio</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Sube tus documentos y llena el DS-160</li>
            <li>• Cuando envíes tu información, nuestro equipo la revisará</li>
            <li>• Llenaremos tu DS-160 oficial en el sistema del consulado</li>
            <li>• Te contactaremos para los siguientes pasos</li>
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            ¿Necesitas ayuda personalizada? <a href="https://wa.me/19548059212?text=Hola%2C%20quiero%20mejorar%20mi%20plan" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Mejora a Premium</a>
          </p>
        </div>

        {/* Success Modal */}
        <AnimatePresence>
          {showSuccessModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50"
                onClick={() => setShowSuccessModal(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-8 text-white text-center">
                    <PartyPopper className="w-16 h-16 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold">¡Información Enviada!</h2>
                  </div>
                  <div className="p-6">
                    <p className="text-center text-navy-700 mb-6">
                      Tu información fue enviada correctamente a nuestro equipo. 
                      <strong> Te contactaremos pronto</strong> para finalizar tu proceso de visa.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-xl mb-6">
                      <p className="text-sm text-blue-700">
                        <strong>¿Qué sigue?</strong>
                        <br />
                        1. Revisaremos tu información
                        <br />
                        2. Llenaremos tu DS-160 oficial
                        <br />
                        3. Te enviaremos tu número de confirmación
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setShowSuccessModal(false);
                        navigate('/dashboard');
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      Ir al Dashboard
                    </Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default AutoRevisionPage;
